import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { FormattedMessage, useIntl } from 'react-intl';
import sortBy from 'lodash/sortBy';
import Spacings from '~/modules/ui-kit/spacings';
import { useCongregations } from '~/hooks/api/useCongregations';
import messages from './messages';
import styles from './styles.module.css';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/users/');

const PublisherFilter = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const query = route.useSearch();
  const { data: { data: congregations = [] } = {}, isLoading: isFetching } =
    useCongregations({
      page: 0,
      perPage: 999999,
    });

  const handleChange =
    (propertyName: string) => (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value;
      navigate({
        to: '/users',
        search: {
          ...query,
          [propertyName]: value === 'all' ? '' : value,
        },
      });
    };
  let role: string[] = [];
  if (Array.isArray(query.role)) {
    role = query.role;
  } else if (query.role) {
    role = [query.role];
  }
  return (
    <Spacings.Stack>
      <div className={styles['filter-list']}>
        <div>
          <FormControl variant="standard">
            <InputLabel htmlFor="congregation">
              <FormattedMessage {...messages.congregation} />
            </InputLabel>
            <Select
              variant="standard"
              className={styles.select}
              value={(!isFetching && query.congregation) || 'all'}
              onChange={handleChange('congregation')}
              inputProps={{
                name: 'congregation',
                id: 'congregation',
              }}
            >
              <MenuItem value="all">
                <FormattedMessage {...messages.allPublishers} />
              </MenuItem>
              {!isFetching
                ? sortBy(congregations, 'name').map((cong) => (
                    <MenuItem key={cong.id} value={cong.id}>
                      {cong.name}
                    </MenuItem>
                  ))
                : null}
            </Select>
          </FormControl>
        </div>
        <div>
          <FormControl variant="standard">
            <InputLabel htmlFor="state">
              <FormattedMessage {...messages.state} />
            </InputLabel>
            <Select
              variant="standard"
              className={styles.select}
              value={query.state || 'all'}
              onChange={handleChange('state')}
              inputProps={{
                name: 'state',
                id: 'state',
              }}
            >
              <MenuItem value="all">
                <FormattedMessage {...messages.allPublishers} />
              </MenuItem>
              <MenuItem value="Active">
                <FormattedMessage {...messages.active} />
              </MenuItem>
              <MenuItem value="Inactive">
                <FormattedMessage {...messages.inactive} />
              </MenuItem>
              <MenuItem value="Invited">
                <FormattedMessage {...messages.invited} />
              </MenuItem>
              <MenuItem value="AtRiskToBecomeInactive">
                <FormattedMessage {...messages.atRiskToBecomeInactive} />
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div>
          <FormControl variant="standard">
            <InputLabel htmlFor="pioneerLevel">
              <FormattedMessage {...messages.pioneerLevel} />
            </InputLabel>
            <Select
              variant="standard"
              className={styles.select}
              value={query.pioneerLevel || 'all'}
              onChange={handleChange('pioneerLevel')}
              inputProps={{
                name: 'pioneerLevel',
                id: 'pioneerLevel',
              }}
            >
              <MenuItem value="all">
                <FormattedMessage {...messages.allPublishers} />
              </MenuItem>
              <MenuItem value="None">
                <FormattedMessage {...messages.none} />
              </MenuItem>
              <MenuItem value="Regular">
                <FormattedMessage {...messages.regularPioneer} />
              </MenuItem>
              <MenuItem value="Special">
                <FormattedMessage {...messages.specialPioneer} />
              </MenuItem>
              <MenuItem value="CircuitOverseer">
                <FormattedMessage {...messages.circuitOverseer} />
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div>
          <FormControl variant="standard">
            <InputLabel htmlFor="privilege">
              <FormattedMessage {...messages.privilege} />
            </InputLabel>
            <Select
              variant="standard"
              className={styles.select}
              value={query.privilege || 'all'}
              onChange={handleChange('privilege')}
              inputProps={{
                name: 'privilege',
                id: 'privilege',
              }}
            >
              <MenuItem value="all">
                <FormattedMessage {...messages.allPublishers} />
              </MenuItem>
              <MenuItem value="None">
                <FormattedMessage {...messages.none} />
              </MenuItem>
              <MenuItem value="Elder">
                <FormattedMessage {...messages.elder} />
              </MenuItem>
              <MenuItem value="MinisterialServant">
                <FormattedMessage {...messages.ministerialServant} />
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div>
          <FormControl variant="standard">
            <InputLabel htmlFor="experienceLevel">
              <FormattedMessage {...messages.experienceLevel} />
            </InputLabel>
            <Select
              variant="standard"
              className={styles.select}
              value={query.experienceLevel || 'all'}
              onChange={handleChange('experienceLevel')}
              inputProps={{
                name: 'experienceLevel',
                id: 'experienceLevel',
              }}
            >
              <MenuItem value="all">
                <FormattedMessage {...messages.allPublishers} />
              </MenuItem>
              <MenuItem value="Newbie">
                <FormattedMessage {...messages.newbie} />
              </MenuItem>
              <MenuItem value="Common">
                <FormattedMessage {...messages.common} />
              </MenuItem>
              <MenuItem value="Mentor">
                <FormattedMessage {...messages.mentor} />
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div>
          <FormControl variant="standard">
            <InputLabel htmlFor="role">
              <FormattedMessage {...messages.role} />
            </InputLabel>
            <Select
              variant="standard"
              multiple
              className={styles.select}
              value={role}
              renderValue={(selectedRoles) =>
                selectedRoles
                  .map((selectedRole) =>
                    intl.formatMessage(
                      messages[selectedRole as keyof typeof messages]
                    )
                  )
                  .join(', ')
              }
              onChange={handleChange('role')}
              inputProps={{
                name: 'role',
                id: 'role',
              }}
            >
              <MenuItem value="isAdministrator">
                <Checkbox checked={role.includes('isAdministrator')} />
                <ListItemText
                  primary={<FormattedMessage {...messages.isAdministrator} />}
                />
              </MenuItem>
              <MenuItem value="isVip">
                <Checkbox checked={role.includes('isVip')} />
                <ListItemText
                  primary={<FormattedMessage {...messages.isVip} />}
                />
              </MenuItem>
              <MenuItem value="isPublisher">
                <Checkbox checked={role.includes('isPublisher')} />
                <ListItemText
                  primary={<FormattedMessage {...messages.isPublisher} />}
                />
              </MenuItem>
              <MenuItem value="isShiftLeader">
                <Checkbox checked={role.includes('isShiftLeader')} />
                <ListItemText
                  primary={<FormattedMessage {...messages.isShiftLeader} />}
                />
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    </Spacings.Stack>
  );
};

export default PublisherFilter;
